import {
  CircleError,
  Close,
  Lens,
  NavigateDown,
  NavigateRight,
} from '@randstad-lean-mobile-factory/react-assets/dist/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import classnames from 'classnames';
import invariant from 'invariant';
import { escapeRegExp, partition, sortBy } from 'lodash-es';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import { Button } from '../Button';
import { Checkbox, TextInput } from '../Forms';
import { InnerRadioButton } from '../Forms/RadioGroup/InnerRadioButton';
import { Loader } from '../Loader/Loader.component';
import { Menu, MenuActions } from '../Menus';
import { pluralFormat } from '../pluralFormat';
import { Tooltip } from '../Tooltip';
import { highlight, normalizeString, parseMutationStatus, usePositionEffect } from '../utils';

import styles from './SmartCombobox.module.scss';
import { SmartComboboxProps } from './SmartCombobox.types';

export const SmartCombobox = <
  TId extends string,
  TItem extends { id: TId; label: string; shortLabel?: string },
  TClearable extends boolean = true,
  TMultiple extends boolean = false,
>({
  size,
  minKeywordLength: _minKeywordLength,
  arrow,
  disabled: _disabled,
  error,
  placeholder: _placeholder,
  multiple,
  combobox,
  clearable,
  showShortLabelSection: _showShortLabelSection,
  hasShortLabel: _hasShortLabel,
  displayPreference: _displayPreference,
  debounceDelay: _debounceDelay,
  selectionSectionTitle: _selectionSectionTitle,
  shortLabelSectionTitle: _shortLabelSectionTitle,
  labelSectionTitle: _labelSectionTitle,
  resultsSectionTitle: _resultsSectionTitle,
  value,
  onChange,
  onKeyDown,
  className,
  ...props
}: SmartComboboxProps<TId, TItem, TClearable, TMultiple>) => {
  const disabled = _disabled || combobox.disabled;
  const minKeywordLength = _minKeywordLength ?? combobox.minKeywordLength ?? 0;
  const debounceDelay = _debounceDelay ?? combobox.debounceDelay ?? 500;
  const showShortLabelSection =
    _showShortLabelSection ??
    combobox.showShortLabelSection ??
    _hasShortLabel ??
    combobox.hasShortLabel ??
    false;
  const displayPreference =
    _displayPreference ??
    combobox.displayPreference ??
    (showShortLabelSection ? 'shortLabel' : 'label');
  const placeholder = _placeholder ?? combobox.placeholder ?? false;
  const selectionSectionTitle =
    _selectionSectionTitle ?? combobox.selectionSectionTitle ?? 'sélection';
  const shortLabelSectionTitle =
    _shortLabelSectionTitle ?? combobox.shortLabelSectionTitle ?? 'code';
  const labelSectionTitle = _labelSectionTitle ?? combobox.labelSectionTitle ?? 'autres résultats';
  const resultsSectionTitle = _resultsSectionTitle ?? combobox.resultsSectionTitle ?? 'résultats';

  const ref = useRef<MenuActions>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [keyword, setKeyword] = useState('');
  useEffect(() => setKeyword(inputValue), [inputValue]);

  const [openSections, setOpenSections] = useState({
    selection: true,
    shortLabel: true,
    label: true,
  });

  const id = useId();

  const queryClient = useQueryClient();

  const details = useQuery({
    queryKey: [id, 'details', value],
    queryFn: async () => {
      invariant(value, 'value is missing');
      const itemIds = multiple ? (value as TId[]) : [value as TId];
      return await combobox.details(itemIds);
    },
    enabled: !!value?.length,
    refetchOnMount: false,
    staleTime: Infinity,
    retry: false,
  });
  const detailsState = parseMutationStatus(details.status);

  const search = useQuery({
    queryKey: [id, 'search', keyword],
    queryFn: async ({ signal }) => {
      if (keyword) await new Promise(res => setTimeout(res, debounceDelay));
      if (signal.aborted) throw signal.reason;
      return await combobox.search(keyword);
    },
    enabled: isOpen && !disabled && keyword.length >= minKeywordLength,
    retry: false,
  });
  const searchState = parseMutationStatus(search.status);

  const [activeItem, setActiveItem] = useState(false);

  const sections = useMemo(() => {
    setActiveItem(false);
    setOpenSections({ selection: true, shortLabel: true, label: true });
    const normalizedKeyword = normalizeString(keyword);
    const keywordRegex = new RegExp(`\\b${escapeRegExp(normalizedKeyword)}`);
    const checkedItems =
      details.data?.map(item => ({
        original: item,
        displayLabel: item.label,
        displayShortLabel: item.shortLabel,
      })) ?? [];
    const searchedItems = sortBy(
      search.data?.filter(item => {
        if (keyword) return true;
        if (multiple) return !value?.includes(item.id);
        return value !== item.id;
      }),
      item => {
        if (!normalizedKeyword) return;
        if (item.shortLabel) {
          const normalizedLabel = normalizeString(item.shortLabel);
          if (normalizedLabel === normalizedKeyword) return 0;
          if (normalizedLabel.includes(normalizedKeyword)) {
            if (normalizedLabel.match(keywordRegex)) return 1;
            return 2;
          }
        }
        const normalizedLabel = normalizeString(item.label);
        if (normalizedLabel === normalizedKeyword) return 3;
        if (normalizedLabel.includes(normalizedKeyword)) {
          if (normalizedLabel.match(keywordRegex)) return 4;
          return 5;
        }
        return 6;
      }
    ).map(item => ({
      original: item,
      displayLabel: highlight(item.label, keyword),
      displayShortLabel: item.shortLabel ? highlight(item.shortLabel, keyword) : undefined,
    }));

    const [shortLabelMatches, labelMatches] = partition(
      searchedItems,
      item =>
        item.original.shortLabel &&
        normalizeString(item.original.shortLabel).includes(normalizedKeyword)
    );

    if (keyword && keyword.length >= minKeywordLength)
      return showShortLabelSection
        ? [
            { id: 'shortLabel' as const, title: shortLabelSectionTitle, items: shortLabelMatches },
            { id: 'label' as const, title: labelSectionTitle, items: labelMatches },
          ]
        : [
            {
              id: 'label' as const,
              title: resultsSectionTitle,
              items: searchedItems,
            },
          ];

    return [
      checkedItems.length && {
        id: 'selection' as const,
        title: selectionSectionTitle,
        items: checkedItems,
      },
      searchedItems.length && {
        id: 'label' as const,
        title: resultsSectionTitle,
        items: searchedItems,
      },
    ].filter(Boolean);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    keyword,
    multiple,
    minKeywordLength,
    showShortLabelSection,
    search.data,
    isOpen,
    shortLabelSectionTitle,
    labelSectionTitle,
    resultsSectionTitle,
    selectionSectionTitle,
  ]);

  const [focusedItem, setFocusedItem] = useState<TItem>();
  useEffect(() => {
    setFocusedItem(sections.flatMap(section => section.items).at(0)?.original);
  }, [sections]);

  const handleClick = useCallback(
    (item: TItem) => () => {
      const queryData = !value?.length
        ? []
        : queryClient.getQueryData<TItem[]>([id, 'details', value]);
      if (multiple) {
        if (value?.includes(item.id)) {
          if (queryData) {
            queryClient.setQueryData<TItem[]>(
              [id, 'details', (value as string[]).filter(id => id !== item.id)],
              queryData.filter(i => i.id !== item.id)
            );
            combobox.setDetailsCallback?.call(
              undefined,
              (value as TId[]).filter(id => id !== item.id),
              queryData.filter(i => i.id !== item.id)
            );
          }
          (onChange as (value: string[], fullValue: TItem[]) => void)?.(
            (value as string[]).filter(id => id !== item.id),
            details.data?.filter(detailItem => detailItem.id !== item.id) ?? []
          );
        } else {
          if (queryData) {
            queryClient.setQueryData<TItem[]>(
              [id, 'details', [...(value ?? []), item.id]],
              queryData.concat(item)
            );
            combobox.setDetailsCallback?.call(
              undefined,
              [...((value ?? []) as TId[]), item.id],
              queryData.concat(item)
            );
          }
          (onChange as (value: string[], fullValue: TItem[]) => void)?.(
            [...(value ?? []), item.id],
            [...(details.data ?? []), item]
          );
        }
      } else {
        if (value === item.id) {
          if (clearable !== false) {
            (onChange as (value: string | undefined, fullValue: TItem | undefined) => void)?.(
              undefined,
              undefined
            );
          }
          setIsOpen(false);
          setInputValue('');
          setKeyword('');
          ref.current?.close();
        } else {
          queryClient.setQueryData<TItem[]>([id, 'details', item.id], [item]);
          combobox.setDetailsCallback?.call(undefined, [item.id], [item]);
          (onChange as (value: string | undefined, fullValue: TItem | undefined) => void)?.(
            item.id,
            item
          );
          setIsOpen(false);
          setInputValue('');
          setKeyword('');
          ref.current?.close();
        }
      }
    },
    [
      clearable,
      combobox.setDetailsCallback,
      details.data,
      id,
      multiple,
      onChange,
      queryClient,
      value,
    ]
  );

  const inputRef = useRef<HTMLInputElement>(null);

  const [bottom, setBottom] = useState(false);
  const [height, setHeight] = useState(0);
  const triggerRef = usePositionEffect(() => {
    if (isOpen) return;
    const boundingBox = triggerRef.current?.getBoundingClientRect();
    const bottom = window.innerHeight - (boundingBox?.bottom ?? 0) > (boundingBox?.top ?? 0);
    const height =
      (bottom ? window.innerHeight - (boundingBox?.bottom ?? 0) : (boundingBox?.top ?? 0)) - 16;
    setHeight(height);
    setBottom(bottom);
  }, [isOpen]);

  return (
    <Menu
      trigger={
        <div
          tabIndex={disabled ? undefined : 0}
          className={classnames(styles.trigger, className, styles[size], {
            [styles.disabled]: disabled,
            [styles.error]: error,
          })}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              if (disabled) return;
              setIsOpen(true);
              ref.current?.open();
            }
            return onKeyDown?.(event);
          }}
          ref={triggerRef}
          {...props}
        >
          <Lens color="blue" />
          <div className={styles.triggerContent}>
            {(!!value?.length && !detailsState.isSuccess
              ? pluralFormat(multiple ? (value?.length ?? 0) : 1, 'élément sélectionné')
              : details.data
                  ?.map(item =>
                    displayPreference === 'shortLabel'
                      ? item.shortLabel || item.label
                      : displayPreference === 'label'
                        ? item.label
                        : item.shortLabel
                          ? `${item.label} (${item.shortLabel})`
                          : item.label
                  )
                  .join(', ')) || <span className={styles.placeholder}>{placeholder}</span>}
          </div>
          {!!value?.length &&
            (detailsState.isPending ? (
              <Loader size="xsmall" />
            ) : detailsState.isError ? (
              <Tooltip
                title={'désolé, une erreur est survenue'}
                containerClassName={styles.tooltip}
              >
                <CircleError variant="fill" color="danger" />
              </Tooltip>
            ) : null)}
          {arrow && <NavigateRight color="blue" />}
        </div>
      }
      onOpen={() => {
        setIsOpen(true);
        setTimeout(() => inputRef.current?.focus());
      }}
      onClose={() => {
        setIsOpen(false);
        setInputValue('');
        setKeyword('');
      }}
      ref={ref}
      anchorOrigin={{ horizontal: 'left', vertical: bottom ? 'bottom' : 'top' }}
      transformOrigin={{ horizontal: 'left', vertical: bottom ? 'top' : 'bottom' }}
      disabled={disabled}
      className={styles.menu}
      // @ts-expect-error ---
      style={{ '--menu-height': height + 'px' }}
    >
      {multiple && (
        <div className={styles.count}>
          {pluralFormat(value?.length ?? 0, 'élément sélectionné')}
        </div>
      )}
      <TextInput
        value={inputValue}
        onValueChange={value => setInputValue(value)}
        autoFocus
        size="medium"
        placeholder={
          minKeywordLength
            ? `${pluralFormat(minKeywordLength, 'caractère')} minimum`
            : 'rechercher...'
        }
        leftIcon={<Lens />}
        rightIcon={
          keyword ? (
            <Button
              variant="tertiary"
              onClick={() => {
                setInputValue('');
                setKeyword('');
              }}
            >
              <Close />
            </Button>
          ) : null
        }
        onKeyDown={event => {
          if (event.key === 'Enter') {
            setActiveItem(true);
          }
        }}
        onKeyUp={event => {
          setActiveItem(false);
          if (event.key === 'Enter') {
            if (!activeItem) return;
            if (focusedItem) handleClick(focusedItem)();
          }
          if (event.key === 'ArrowDown') {
            if (focusedItem) {
              const items = sections.flatMap(section => section.items);
              const focusIndex = items.findIndex(item => item.original.id === focusedItem.id);
              if (focusIndex + 1 < items.length) {
                setFocusedItem(items[focusIndex + 1].original);
                setTimeout(() => {
                  inputRef.current?.parentElement?.parentElement
                    ?.querySelector('.' + styles.focused)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                });
              }
            }
          }
          if (event.key === 'ArrowUp') {
            if (focusedItem) {
              const items = sections.flatMap(section => section.items);
              const focusIndex = items.findIndex(item => item.original.id === focusedItem.id);
              if (focusIndex >= 1) {
                setFocusedItem(items[focusIndex - 1].original);
                setTimeout(() => {
                  inputRef.current?.parentElement?.parentElement
                    ?.querySelector('.' + styles.focused)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                });
              }
            }
          }
        }}
        containerClassName={styles.input}
        ref={inputRef}
      />
      <div className={styles.results}>
        {(searchState.isPending && keyword.length >= minKeywordLength) ||
        (!!value?.length && detailsState.isPending) ? (
          <Loader size="small" />
        ) : (searchState.isError && keyword.length >= minKeywordLength) ||
          (!!value?.length && detailsState.isError) ? (
          <div className={styles.emptySection}>désolé, une erreur est survenue</div>
        ) : (
          sections.map((section, index) => (
            <div key={index} className={styles.section}>
              <button
                className={classnames(styles.sectionTitle, {
                  [styles.openSection]: openSections[section.id],
                })}
                onClick={() =>
                  setOpenSections(openSections => ({
                    ...openSections,
                    [section.id]: !openSections[section.id],
                  }))
                }
              >
                <span>{section.title}</span>
                <NavigateDown />
              </button>
              {!openSections[section.id] ? null : !section.items.length ? (
                <div className={styles.emptySection}>aucun résultat pour "{keyword}"</div>
              ) : (
                section.items.map(item => (
                  <Tooltip
                    title={item.original.label}
                    enterDelay={2000}
                    disableInteractive
                    key={item.original.id}
                  >
                    <button
                      className={classnames(styles.item, {
                        [styles.selected]: multiple
                          ? value?.includes(item.original.id)
                          : value === item.original.id,
                        [styles.focused]: item.original.id === focusedItem?.id,
                        [styles.active]: item.original.id === focusedItem?.id && activeItem,
                      })}
                      onClick={handleClick(item.original)}
                    >
                      <span className={styles.checkboxContainer}>
                        {multiple ? (
                          <Checkbox
                            tabIndex={-1}
                            checked={value?.includes(item.original.id) ?? false}
                            onChange={() => {}}
                          />
                        ) : (
                          <InnerRadioButton
                            tabIndex={-1}
                            checked={value === item.original.id}
                            onChange={() => {}}
                          />
                        )}
                      </span>
                      <span
                        className={styles.label}
                        dangerouslySetInnerHTML={{ __html: item.displayLabel }}
                      />
                      {item.displayShortLabel ? (
                        <span className={styles.shortLabel}>
                          (<span dangerouslySetInnerHTML={{ __html: item.displayShortLabel }} />)
                        </span>
                      ) : null}
                    </button>
                  </Tooltip>
                ))
              )}
            </div>
          ))
        )}
      </div>
    </Menu>
  );
};
